import { Link } from "react-router-dom";
import LegalPage, { LegalSection } from "@/pages/legal/LegalPage";

const EFFECTIVE_DATE = "February 5, 2026";

export default function Terms() {
  return (
    <LegalPage
      testId="terms-page"
      eyebrow="Legal"
      title="Terms of Service"
      effectiveDate={EFFECTIVE_DATE}
    >
      <p>
        Welcome to PostShifted. These Terms of Service (&ldquo;<strong>Terms</strong>&rdquo;) govern
        your access to and use of the PostShifted website, application, and related services
        (together, the &ldquo;<strong>Service</strong>&rdquo;). By creating an account or using
        the Service, you agree to be bound by these Terms.
      </p>

      <LegalSection n={1} title="Acceptance of terms">
        <p>
          By accessing or using the Service, you confirm that you have read, understood, and agreed
          to these Terms and to our{" "}
          <Link to="/privacy" className="text-coral hover:text-coral-hover">Privacy Policy</Link>{" "}
          and{" "}
          <Link to="/refund" className="text-coral hover:text-coral-hover">Refund Policy</Link>.
          If you do not agree, you may not use the Service.
        </p>
        <p>
          You must be at least 16 years old (or the age of digital consent in your jurisdiction) to
          use the Service. If you are using the Service on behalf of an organization, you represent
          that you have authority to bind that organization to these Terms.
        </p>
      </LegalSection>

      <LegalSection n={2} title="Description of the Service">
        <p>
          PostShifted is an AI-assisted content-repurposing tool. You provide long-form input
          (blog post, transcript, notes, etc.) and the Service produces platform-native drafts —
          such as Twitter/X threads, LinkedIn posts, LinkedIn carousels, Instagram captions, and
          email newsletters — using third-party large language models (currently Anthropic Claude
          Haiku 4.5).
        </p>
        <p>
          Features may be added, changed, or removed at any time. We aim to give notice of material
          changes to paid features but reserve the right to iterate on the product without prior
          notice for free-tier features.
        </p>
      </LegalSection>

      <LegalSection n={3} title="Your account">
        <p>
          You are responsible for maintaining the confidentiality of your account credentials and
          for all activity that occurs under your account. Notify us immediately at{" "}
          <a href="mailto:support@postshifted.com" className="text-coral hover:text-coral-hover">
            support@postshifted.com
          </a>{" "}
          if you suspect unauthorized use.
        </p>
        <p>
          One person may hold one account. Sharing accounts, reselling access, or automating
          signups is prohibited.
        </p>
      </LegalSection>

      <LegalSection n={4} title="User responsibilities and acceptable use">
        <p>You agree that you will not use the Service to:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>Submit content you do not own or have permission to remix.</li>
          <li>Generate content that is unlawful, defamatory, harassing, hateful, or that infringes any third-party rights.</li>
          <li>Impersonate any person or entity, or misrepresent your affiliation.</li>
          <li>Attempt to reverse-engineer, resell, or systematically scrape the Service.</li>
          <li>Use the Service to build a directly competing product.</li>
          <li>Circumvent usage limits, pricing, or authentication controls.</li>
        </ul>
        <p>
          You remain responsible for reviewing all AI-generated output before publishing it. The
          Service can make mistakes and is not a substitute for editorial judgment or legal review.
        </p>
      </LegalSection>

      <LegalSection n={5} title="Ownership of content">
        <p>
          <strong>Your input</strong> and <strong>your generated outputs</strong> belong to you.
          You retain all rights to the content you paste in and to the drafts the Service returns.
          By using the Service you grant PostShifted a limited licence to process your content for
          the purpose of providing and improving the Service, including transmitting it to the
          underlying AI provider.
        </p>
        <p>
          The Service itself, including its interface, brand, templates, code, and documentation,
          remains the intellectual property of PostShifted.
        </p>
      </LegalSection>

      <LegalSection n={6} title="Payments and subscriptions">
        <p>
          The Service is offered on a Free tier and a paid <strong>Pro</strong> subscription
          ($19 USD per month, billed monthly, prices as displayed at checkout). Every new Pro
          subscription starts with a <strong>7-day free trial</strong>; your first $19 charge lands
          on day 8 unless you cancel before then.
        </p>
        <p>
          Billing is processed by <strong>PayPal</strong>. When you subscribe to Pro, you complete
          the payment on PayPal&apos;s hosted checkout and PayPal charges your PayPal account or
          connected card on each renewal. Your use of PayPal at checkout is subject to PayPal&apos;s
          own{" "}
          <a
            href="https://www.paypal.com/us/legalhub/useragreement-full"
            target="_blank"
            rel="noreferrer noopener"
            className="text-coral hover:text-coral-hover"
          >
            User Agreement
          </a>
          .
        </p>
        <p>
          Subscriptions auto-renew each billing cycle until cancelled. Prices may change with at
          least 30 days&apos; notice; changes will not apply retroactively to a billing cycle you
          have already paid for.
        </p>
      </LegalSection>

      <LegalSection n={7} title="Cancellation">
        <p>
          You may cancel your Pro subscription at any time from the <strong>Settings</strong> page
          via the &ldquo;Cancel subscription&rdquo; button. You can also cancel directly from your
          PayPal account under <em>Settings → Payments → Manage automatic payments</em>.
        </p>
        <p>
          When you cancel, you retain Pro access until the end of the current billing period, after
          which the account reverts to the Free tier. Cancellation does not entitle you to a refund
          for the current period except as described in our{" "}
          <Link to="/refund" className="text-coral hover:text-coral-hover">Refund Policy</Link>.
        </p>
      </LegalSection>

      <LegalSection n={8} title="Service availability">
        <p>
          We aim for high availability but do not guarantee uninterrupted service. Planned
          maintenance, third-party AI-provider outages, and factors outside our control may cause
          temporary disruption. We do not offer a formal SLA on the Free tier.
        </p>
      </LegalSection>

      <LegalSection n={9} title="Third-party services">
        <p>
          The Service depends on third-party providers, including AI model providers, payment
          processors, and cloud infrastructure. Your use of the Service is subject to those
          providers&apos; terms in addition to ours. We are not responsible for third-party
          services we do not operate.
        </p>
      </LegalSection>

      <LegalSection n={10} title="Disclaimer of warranties">
        <p>
          The Service is provided &ldquo;<strong>as is</strong>&rdquo; and &ldquo;<strong>as available</strong>&rdquo;.
          To the maximum extent permitted by law, PostShifted disclaims all warranties, express or
          implied, including merchantability, fitness for a particular purpose, and non-infringement.
          AI outputs may be inaccurate, incomplete, or unsuitable for your use case — you are
          responsible for reviewing them.
        </p>
      </LegalSection>

      <LegalSection n={11} title="Limitation of liability">
        <p>
          To the maximum extent permitted by law, PostShifted, its owners, and its contributors
          will not be liable for any indirect, incidental, consequential, special, or punitive
          damages, including lost profits, lost data, lost goodwill, or business interruption,
          arising from your use of the Service.
        </p>
        <p>
          Our aggregate liability for any claim relating to the Service is capped at the greater of
          (a) the amount you paid us in the twelve months preceding the claim, or (b) USD 50.
        </p>
      </LegalSection>

      <LegalSection n={12} title="Termination">
        <p>
          We may suspend or terminate your account if you breach these Terms, misuse the Service,
          or if we are required to do so by law. You may terminate your account at any time by
          emailing us — data-deletion timelines are covered in our Privacy Policy.
        </p>
      </LegalSection>

      <LegalSection n={13} title="Changes to these Terms">
        <p>
          We may update these Terms occasionally. Material changes will be announced in-app or by
          email at least 14 days before they take effect. Continued use of the Service after the
          effective date constitutes acceptance of the updated Terms.
        </p>
      </LegalSection>

      <LegalSection n={14} title="Governing law">
        <p>
          These Terms are governed by the laws of the <strong>Cayman Islands</strong>, without
          regard to conflict-of-law principles. Any disputes will be resolved in the competent
          courts of the Cayman Islands, unless a mandatory consumer-protection law of your country
          of residence requires otherwise.
        </p>
      </LegalSection>

      <LegalSection n={15} title="Contact">
        <p>
          Questions about these Terms? Reach us at{" "}
          <a href="mailto:support@postshifted.com" className="text-coral hover:text-coral-hover">
            support@postshifted.com
          </a>
          .
        </p>
      </LegalSection>
    </LegalPage>
  );
}
